import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, AlertCircle, TrendingUp, TrendingDown, Minus, ExternalLink, Sparkles, Lock, X, LogIn } from "lucide-react";
import { Card, Badge, SectionLabel } from "../components/ui-bits";
import { Skeleton } from "../components/ui/skeleton";
import PriceChart from "../components/PriceChart";
import { liveAPI } from "../data/liveApi";
import { ga } from "../analytics/ga";
import { useAuth } from "../auth/AuthContext";
import { SeoHead } from "../seo/SeoHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stockAPI = {
  search: (q) => axios.get(`${API}/stocks/search`, { params: { q } }).then((r) => r.data),
  snapshot: (symbol) => axios.get(`${API}/stocks/${symbol}`).then((r) => r.data),
  history: (symbol, range) => axios.get(`${API}/stocks/${symbol}/history`, { params: { range } }).then((r) => r.data),
  analyze: (symbol) => axios.post(`${API}/stocks/${symbol}/analysis`, {}, { timeout: 90_000 }).then((r) => r.data),
  usage: () => axios.get(`${API}/stocks/usage`).then((r) => r.data),
};

function formatIndianNumber(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

function formatCrore(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const cr = v / 1e7;
  if (cr >= 1e5) return `₹${(cr / 1e5).toFixed(2)} L Cr`;
  return `₹${cr.toLocaleString("en-IN", { maximumFractionDigits: 0 })} Cr`;
}

/* --- Search box with autocomplete --- */
function StockSearch({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const t = useRef(null);

  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (!q || q.length < 1) { setResults([]); return; }
    setLoading(true);
    t.current = setTimeout(async () => {
      try {
        const d = await stockAPI.search(q);
        setResults(d.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 180);
    return () => t.current && clearTimeout(t.current);
  }, [q]);

  const pick = (s) => {
    setQ("");
    setResults([]);
    setOpen(false);
    onSelect(s.symbol);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-fij-bg border border-fij-border rounded-sm px-3 h-11">
        <Search size={16} className="text-fij-textMuted shrink-0" />
        <input
          data-testid="stock-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search NSE stock..."
          className="bg-transparent outline-none w-full font-mono text-sm text-fij-text placeholder:text-fij-textMuted"
        />
        {loading && <Loader2 size={14} className="text-fij-textMuted animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div
          data-testid="stock-search-dropdown"
          className="absolute top-12 left-0 right-0 z-30 border border-fij-border bg-fij-surface rounded-sm shadow-xl max-h-80 overflow-y-auto"
        >
          {results.map((s) => (
            <button
              key={s.symbol}
              data-testid={`stock-search-result-${s.symbol}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-fij-green/[0.06] border-b border-fij-border/40 last:border-0 text-left"
            >
              <div className="min-w-0">
                <div className="font-mono text-sm text-fij-text truncate">{s.symbol}</div>
                <div className="font-mono text-[11px] text-fij-textDim truncate">{s.name}</div>
              </div>
              <span className="font-mono text-[10px] text-fij-textMuted shrink-0">{s.sector}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* --- Snapshot card --- */
function SnapshotCard({ snap, loading }) {
  if (loading) {
    return (
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-20 bg-fij-border/40" />
              <Skeleton className="h-5 w-24 bg-fij-border/40" />
            </div>
          ))}
        </div>
      </Card>
    );
  }
  if (!snap?.available) {
    return (
      <Card className="border-fij-red/30 bg-fij-red/5" data-testid="stock-snapshot-error">
        <div className="flex items-start gap-3">
          <AlertCircle size={16} className="text-fij-red shrink-0 mt-0.5" />
          <div className="font-mono text-sm text-fij-red">Data currently unavailable</div>
        </div>
      </Card>
    );
  }
  const chg = snap.change || 0;
  const chgPct = snap.change_pct || 0;
  const positive = chg >= 0;
  const chgColor = positive ? "text-fij-green" : "text-fij-red";

  const cells = [
    { label: "Company Name", value: snap.name, testid: "snap-name", className: "sm:col-span-2 lg:col-span-2" },
    { label: "NSE Symbol", value: snap.symbol, testid: "snap-symbol" },
    { label: "Sector", value: snap.sector, testid: "snap-sector" },
    { label: "Industry", value: snap.industry, testid: "snap-industry" },
    { label: "Current Price", value: `₹${formatIndianNumber(snap.price)}`, testid: "snap-price", className: chgColor },
    { label: "Today's Change", value: `${positive ? "+" : ""}${formatIndianNumber(chg)} (${chgPct.toFixed(2)}%)`, className: chgColor, testid: "snap-change" },
    { label: "Market Cap", value: formatCrore(snap.market_cap), testid: "snap-mcap" },
    { label: "52 Week High", value: `₹${formatIndianNumber(snap.fifty_two_week_high)}`, testid: "snap-52h" },
    { label: "52 Week Low", value: `₹${formatIndianNumber(snap.fifty_two_week_low)}`, testid: "snap-52l" },
    { label: "PE Ratio", value: snap.pe_ratio != null ? Number(snap.pe_ratio).toFixed(2) : "—", testid: "snap-pe" },
  ];

  return (
    <Card data-testid="stock-snapshot">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {cells.map((c) => (
          <div key={c.label} className={c.className || ""}>
            <div className="text-[10px] uppercase text-fij-textMuted font-mono">{c.label}</div>
            <div data-testid={c.testid} className={`font-mono text-sm text-fij-text mt-1 truncate ${c.className || ""}`}>
              {c.value}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* --- Verdict card --- */
const VERDICT_META = {
  "Strong Buy": { tone: "positive", color: "text-fij-green", border: "border-fij-green/60" },
  "Buy":        { tone: "positive", color: "text-fij-green", border: "border-fij-green/40" },
  "Hold":       { tone: "neutral",  color: "text-fij-textDim", border: "border-fij-border" },
  "Reduce":     { tone: "warning",  color: "text-fij-amber", border: "border-fij-amber/40" },
  "Sell":       { tone: "negative", color: "text-fij-red",   border: "border-fij-red/40" },
};

function VerdictCard({ analysis }) {
  const meta = VERDICT_META[analysis.verdict] || VERDICT_META["Hold"];
  return (
    <Card className={`border-l-2 ${meta.border}`} data-testid="verdict-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <SectionLabel>INVESTMENT_VERDICT</SectionLabel>
          <div className={`fij-mono text-2xl sm:text-3xl mt-1 ${meta.color}`} data-testid="verdict-value">
            {analysis.verdict.toUpperCase()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-fij-textMuted font-mono">Confidence</div>
          <div className="fij-mono text-2xl text-fij-text" data-testid="verdict-confidence">{analysis.confidence}%</div>
        </div>
      </div>
      {analysis.verdict_reason && (
        <p className="text-fij-textDim text-sm mt-3 leading-relaxed" data-testid="verdict-reason">
          <span className="text-fij-green font-mono mr-2">›</span>{analysis.verdict_reason}
        </p>
      )}
    </Card>
  );
}

/* --- News with sentiment --- */
const SENTI_TONE = { positive: "positive", negative: "negative", neutral: "neutral" };

function NewsList({ news }) {
  if (!news?.length) {
    return <Card className="text-center py-6 font-mono text-xs text-fij-textMuted">No recent news.</Card>;
  }
  return (
    <div className="space-y-2" data-testid="stock-news">
      {news.map((n, i) => (
        <a
          key={i}
          href={n.url || "#"}
          target={n.url ? "_blank" : undefined}
          rel="noreferrer"
          data-testid={`stock-news-${i}`}
          className="block border border-fij-border rounded-sm p-3 hover:border-fij-green/30 transition-colors group"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge tone={SENTI_TONE[n.sentiment] || "neutral"}>{n.sentiment || "neutral"}</Badge>
                <span className="font-mono text-[10px] text-fij-textMuted">
                  {n.source || "—"} · {n.published_at ? new Date(n.published_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </span>
              </div>
              <div className="text-sm text-fij-text group-hover:text-fij-green transition-colors line-clamp-2">
                {n.headline}
              </div>
            </div>
            {n.url && <ExternalLink size={12} className="text-fij-textMuted group-hover:text-fij-green shrink-0 mt-1" />}
          </div>
        </a>
      ))}
    </div>
  );
}

/* --- Upgrade Popup --- */
function UpgradePopup({ onClose, onUpgrade }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm" data-testid="upgrade-popup">
      <Card className="max-w-md mx-4 border-fij-green/40">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-fij-amber" />
            <SectionLabel className="!mt-0">DAILY_LIMIT_REACHED</SectionLabel>
          </div>
          <button onClick={onClose} data-testid="upgrade-popup-close" className="text-fij-textMuted hover:text-fij-text">
            <X size={16} />
          </button>
        </div>
        <h3 className="font-mono text-lg text-fij-text">You have used today&#39;s free analyses.</h3>
        <p className="text-fij-textDim text-sm mt-2 leading-relaxed">
          Upgrade to <span className="text-fij-green font-medium">FIJ Pro</span> for unlimited AI stock research.
        </p>
        <div className="flex items-center gap-2 mt-5">
          <button
            data-testid="upgrade-button"
            onClick={onUpgrade}
            className="flex-1 bg-fij-green text-black font-mono text-sm py-2 rounded-sm hover:bg-fij-green/90 transition-colors"
          >
            UPGRADE
          </button>
          <button
            data-testid="upgrade-later-button"
            onClick={onClose}
            className="flex-1 border border-fij-border font-mono text-sm py-2 rounded-sm text-fij-textDim hover:text-fij-text transition-colors"
          >
            MAYBE LATER
          </button>
        </div>
      </Card>
    </div>
  );
}

/* --- Main page --- */
export default function StockAnalysis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [symbol, setSymbol] = useState(null);
  const [snap, setSnap] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [snapErr, setSnapErr] = useState(null);
  const [aiErr, setAiErr] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [aiBlocked, setAiBlocked] = useState(false);

  // Load usage on mount — only for logged-in users (guests have no quota row).
  useEffect(() => {
    if (!user) return;
    stockAPI.usage().then(setUsage).catch(() => {});
  }, [user]);

  const selectStock = async (sym) => {
    setSymbol(sym);
    ga.stockSearch(sym);
    setSnap(null); setAnalysis(null);
    setSnapErr(null); setAiErr(null); setAiBlocked(false);
    setLoadingSnap(true);
    try {
      const s = await stockAPI.snapshot(sym);
      setSnap(s);
    } catch (e) {
      setSnapErr(e?.response?.data?.detail || "Data currently unavailable");
    } finally { setLoadingSnap(false); }

    // AI analysis requires an account — guests see the snapshot + chart only.
    if (!user) {
      setAiBlocked(true);
      return;
    }
    setLoadingAI(true);
    try {
      const a = await stockAPI.analyze(sym);
      setAnalysis(a);
      ga.stockAnalysis(sym, Boolean(a?.cached));
      // refresh usage
      stockAPI.usage().then(setUsage).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 429) {
        setAiBlocked(true);
        setShowUpgrade(true);
        stockAPI.usage().then(setUsage).catch(() => {});
      } else {
        setAiErr(e?.response?.data?.detail || "Data currently unavailable");
      }
    } finally { setLoadingAI(false); }
  };

  return (
    <div className="space-y-6" data-testid="stock-analysis-page">
      <SeoHead path="/stock-analysis" />
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex-1 lg:max-w-2xl">
          <StockSearch onSelect={selectStock} />
        </div>
        {user && usage && (
          <div className="font-mono text-[10px] text-fij-textMuted shrink-0" data-testid="stock-usage">
            <Sparkles size={10} className="inline text-fij-green mr-1" />
            {usage.remaining}/{usage.limit} FREE ANALYSES · RESETS {usage.resets_at_ist}
          </div>
        )}
        {!user && (
          <div className="font-mono text-[10px] text-fij-textMuted shrink-0" data-testid="stock-usage-guest">
            <Lock size={10} className="inline text-fij-amber mr-1" />
            SIGN IN FOR AI ANALYSIS · 3 FREE / DAY
          </div>
        )}
      </div>

      {!symbol && (
        <Card className="text-center py-16" data-testid="stock-empty-state">
          <div className="font-mono text-sm text-fij-textDim">
            Search a stock above to begin. E.g., <span className="text-fij-text">RELIANCE</span>,
            <span className="text-fij-text"> TCS</span>, <span className="text-fij-text">HDFCBANK</span>.
          </div>
        </Card>
      )}

      {symbol && (
        <>
          <SnapshotCard snap={snap} loading={loadingSnap} />
          {snapErr && !snap && (
            <Card className="border-fij-red/30 bg-fij-red/5">
              <div className="font-mono text-sm text-fij-red">Data currently unavailable</div>
            </Card>
          )}

          {snap?.available && (
            <PriceChart
              symbol={symbol}
              label={snap.name}
              ranges={["1D", "1W", "1M", "3M", "YTD", "1Y"]}
              fetchHistory={stockAPI.history}
              cardTitle="STOCK PRICE"
              className=""
            />
          )}

          {/* AI Summary section (blurred when blocked) */}
          <div className="relative">
            <Card data-testid="ai-summary-card">
              <div className={aiBlocked ? "blur-sm pointer-events-none select-none" : ""}>
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-fij-green" />
                  <SectionLabel className="!mt-0">AI SUMMARY · CLAUDE_SONNET</SectionLabel>
                </div>
                {loadingAI && !analysis ? (
                  <div className="mt-3 space-y-2">
                    <Skeleton className="h-3 w-full bg-fij-border/40" />
                    <Skeleton className="h-3 w-5/6 bg-fij-border/40" />
                    <Skeleton className="h-3 w-4/6 bg-fij-border/40" />
                  </div>
                ) : aiErr ? (
                  <div className="mt-3 font-mono text-sm text-fij-red">Data currently unavailable</div>
                ) : analysis ? (
                  <p className="text-fij-text text-sm mt-3 leading-relaxed" data-testid="ai-summary-text">
                    {analysis.summary}
                  </p>
                ) : null}
              </div>
              {aiBlocked && (
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <Lock size={20} className="mx-auto text-fij-amber" />
                    {!user ? (
                      <>
                        <div className="font-mono text-xs text-fij-amber mt-2" data-testid="ai-guest-lock">
                          Sign in to unlock AI Analysis
                        </div>
                        <button
                          onClick={() => navigate("/login")}
                          data-testid="ai-guest-signin"
                          className="mt-3 inline-flex items-center gap-1.5 font-mono text-xs text-fij-green border border-fij-green/40 px-3 py-1.5 rounded-sm hover:bg-fij-green/10 transition-colors"
                        >
                          <LogIn size={12} /> SIGN IN — IT'S FREE
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="font-mono text-xs text-fij-amber mt-2">Daily free analyses used</div>
                        <button
                          onClick={() => { ga.upgradeClick("blocked_summary"); setShowUpgrade(true); }}
                          data-testid="ai-blocked-upgrade"
                          className="mt-3 inline-flex items-center gap-1 font-mono text-xs text-fij-green border border-fij-green/40 px-3 py-1.5 rounded-sm hover:bg-fij-green/10 transition-colors"
                        >
                          UPGRADE TO FIJ PRO
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {analysis && !aiBlocked && (
            <>
              <VerdictCard analysis={analysis} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card data-testid="strengths-card">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={14} className="text-fij-green" />
                    <SectionLabel className="!mt-0">STRENGTHS</SectionLabel>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(analysis.strengths || []).map((s, i) => (
                      <li key={i} data-testid={`strength-${i}`} className="flex items-start gap-2 text-sm text-fij-text">
                        <span className="text-fij-green font-mono mt-0.5">+</span><span>{s}</span>
                      </li>
                    ))}
                    {!analysis.strengths?.length && <li className="font-mono text-xs text-fij-textMuted italic">—</li>}
                  </ul>
                </Card>

                <Card data-testid="risks-card">
                  <div className="flex items-center gap-2">
                    <TrendingDown size={14} className="text-fij-red" />
                    <SectionLabel className="!mt-0">RISKS</SectionLabel>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(analysis.risks || []).map((s, i) => (
                      <li key={i} data-testid={`risk-${i}`} className="flex items-start gap-2 text-sm text-fij-text">
                        <span className="text-fij-red font-mono mt-0.5">−</span><span>{s}</span>
                      </li>
                    ))}
                    {!analysis.risks?.length && <li className="font-mono text-xs text-fij-textMuted italic">—</li>}
                  </ul>
                </Card>
              </div>

              <Card>
                <SectionLabel>LATEST_NEWS</SectionLabel>
                <div className="mt-3"><NewsList news={analysis.news} /></div>
              </Card>
            </>
          )}
        </>
      )}

      {showUpgrade && (
        <UpgradePopup
          onClose={() => setShowUpgrade(false)}
          onUpgrade={() => {
            ga.upgradeClick("stock_analysis_quota");
            window.alert("FIJ Pro upgrade — coming soon.");
            setShowUpgrade(false);
          }}
        />
      )}
    </div>
  );
}
